"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalDataSealer = exports.LOCAL_DATA_ACCESS_ERROR_CODES = exports.LOCAL_DATA_KINDS = void 0;
exports.localAccountKeyError = localAccountKeyError;
exports.importLocalDataKek = importLocalDataKek;
exports.wrapLocalDocKey = wrapLocalDocKey;
exports.unwrapLocalDocKey = unwrapLocalDocKey;
/**
 * Sealing of local data: AES-256-GCM records under a document key that an
 * AES-KW key encryption key wraps. One implementation for the local data of
 * StreamLain (nbstore) and the writes an App keeps offline (app-local-store);
 * no persistence and no key export here.
 */
exports.LOCAL_DATA_KINDS = [
    'update',
    'snapshot',
    'publication-member',
    'journal',
    'index',
    'blob',
    'blob-metadata',
    /** A write an App keeps offline (view-config app-local-store). */
    'app-write',
];
exports.LOCAL_DATA_ACCESS_ERROR_CODES = [
    'LOCKED',
    'DENIED',
    'CORRUPT',
    'KEY_UNAVAILABLE',
    /** Local data was written by a newer app version; it is never touched. */
    'LOCAL_STORE_NEWER',
];
function localAccountKeyError(code) {
    // Never include input, keys, envelopes or proofs in an error message.
    return Object.assign(new Error(`Local account key access: ${code}`), {
        code,
    });
}
/** The account or App key encryption key the server unwrapped, kept only as a
 * non-extractable key. */
async function importLocalDataKek(raw) {
    if (raw.byteLength !== 32)
        throw localAccountKeyError('KEY_UNAVAILABLE');
    const bytes = new Uint8Array(raw);
    try {
        return await crypto.subtle.importKey('raw', bytes.buffer, { name: 'AES-KW' }, false, ['wrapKey', 'unwrapKey']);
    }
    finally {
        bytes.fill(0);
    }
}
function assertKey(key, name) {
    const usages = name === 'AES-GCM' ? ['encrypt', 'decrypt'] : ['wrapKey', 'unwrapKey'];
    if (!key ||
        key.type !== 'secret' ||
        key.extractable ||
        key.algorithm.name !== name ||
        !('length' in key.algorithm) ||
        key.algorithm.length !== 256 ||
        !usages.every(usage => key.usages.includes(usage)))
        throw localAccountKeyError('KEY_UNAVAILABLE');
}
/** Only the short-lived generation key is extractable; retain only its wrapped bytes. */
async function wrapLocalDocKey(kek) {
    assertKey(kek, 'AES-KW');
    let generated = null;
    try {
        generated = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
        return new Uint8Array(await crypto.subtle.wrapKey('raw', generated, kek, 'AES-KW'));
    }
    catch {
        throw localAccountKeyError('KEY_UNAVAILABLE');
    }
    finally {
        generated = null;
    }
}
async function unwrapLocalDocKey(kek, wrapped) {
    assertKey(kek, 'AES-KW');
    if (!(wrapped instanceof Uint8Array) || wrapped.byteLength !== 40)
        throw localAccountKeyError('CORRUPT');
    try {
        return await crypto.subtle.unwrapKey('raw', new Uint8Array(wrapped), kek, 'AES-KW', { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    }
    catch {
        throw localAccountKeyError('CORRUPT');
    }
}
/** No persistence, key export or retained unwrapped-key cache in the SealPort itself. */
class LocalDataSealer {
    constructor(keys) {
        this.keys = keys;
    }
    async seal(scope, plain) {
        const identity = this.scopeIdentity(scope);
        const payload = new Uint8Array(plain);
        try {
            const { keyId, key } = await this.getKey(identity);
            const iv = crypto.getRandomValues(new Uint8Array(12));
            try {
                const ciphertext = await crypto.subtle.encrypt({
                    name: 'AES-GCM',
                    iv,
                    additionalData: this.aad(identity, keyId),
                    tagLength: 128,
                }, key, payload);
                return {
                    version: 1,
                    keyId,
                    iv,
                    ciphertext: new Uint8Array(ciphertext),
                };
            }
            catch {
                throw localAccountKeyError('KEY_UNAVAILABLE');
            }
        }
        finally {
            payload.fill(0);
        }
    }
    async open(scope, record) {
        const identity = this.scopeIdentity(scope);
        if (!record ||
            record.version !== 1 ||
            typeof record.keyId !== 'string' ||
            !record.keyId ||
            !(record.iv instanceof Uint8Array) ||
            record.iv.byteLength !== 12 ||
            !(record.ciphertext instanceof Uint8Array) ||
            record.ciphertext.byteLength < 16)
            throw localAccountKeyError('CORRUPT');
        // Copy before awaiting custody so caller mutation cannot change the authenticated input.
        const recordKeyId = record.keyId;
        const iv = new Uint8Array(record.iv);
        const ciphertext = new Uint8Array(record.ciphertext);
        const { keyId, key } = await this.getKey(identity);
        if (recordKeyId !== keyId)
            throw localAccountKeyError('KEY_UNAVAILABLE');
        try {
            const plain = await crypto.subtle.decrypt({
                name: 'AES-GCM',
                iv,
                additionalData: this.aad(identity, keyId),
                tagLength: 128,
            }, key, ciphertext);
            return new Uint8Array(plain);
        }
        catch {
            throw localAccountKeyError('CORRUPT');
        }
    }
    async getKey(scope) {
        try {
            const result = await this.keys.getKey(scope);
            if (!result || typeof result.keyId !== 'string' || !result.keyId)
                throw localAccountKeyError('KEY_UNAVAILABLE');
            assertKey(result.key, 'AES-GCM');
            return result;
        }
        catch (error) {
            const code = error && typeof error === 'object' && 'code' in error
                ? error.code
                : null;
            if (code === 'LOCKED' || code === 'DENIED' || code === 'CORRUPT')
                throw localAccountKeyError(code);
            throw localAccountKeyError('KEY_UNAVAILABLE');
        }
    }
    scopeIdentity(scope) {
        if (!scope || !exports.LOCAL_DATA_KINDS.includes(scope.kind))
            throw localAccountKeyError('CORRUPT');
        const { accountId, workspaceId, docId, kind, revision } = scope;
        if (![accountId, workspaceId, docId, kind, revision].every(value => typeof value === 'string' && value.length > 0))
            throw localAccountKeyError('CORRUPT');
        return { accountId, workspaceId, docId, kind, revision };
    }
    aad(scope, keyId) {
        return new TextEncoder().encode(JSON.stringify([
            'streamlain-local-data-v1',
            1,
            keyId,
            scope.accountId,
            scope.workspaceId,
            scope.docId,
            scope.kind,
            scope.revision,
        ]));
    }
}
exports.LocalDataSealer = LocalDataSealer;
